import * as React from 'react';
import { useSelector } from 'react-redux';

// eslint-disable-next-line import/no-restricted-paths
import { removeCookie } from '../../../../browser/utils/js-cookie';
import { EBannerType } from '../../../constants/bannerType';
import { DiscountMortgageBannerContainer } from '../../../containers/NewbuildingDiscountMortgageBanner';
import { NewbuildingFlatSaleBannerContainer } from '../../../containers/NewbuildingFlatSaleBannerContainer';
import { NewbuildingRentBannerContainer } from '../../../containers/NewbuildingRentBannerContainer';
import { NewbuildingSuburbanSaleBannerContainer } from '../../../containers/NewbuildingSuburbanSaleBannerContainer';
import { getDealType } from '../../../selectors/getDealType';
import { getJsonQuery } from '../../../selectors/getJsonQuery';
import { getNewbuildingSimilarAdPath } from '../../../selectors/getNewbuildingSimilarAdPath';
import { getNewbuildingSimilarOffers } from '../../../selectors/getNewbuildingSimilarOffers';
import { getOfferType } from '../../../selectors/getOfferType';
import { selectIsNewbuildingOrMixedListing } from '../../../selectors/newbuildingBannerCommon';
import { selectNewbuildingBrokerConsultantBannerAvailable } from '../../../selectors/newbuildingBrokerConsultantBanner';
import { getIsNewbuildingBannerRotationEnabled } from '../../../selectors/newbuildingEntryBanner';
import { selectNewbuildingFlightBannerAvailable } from '../../../selectors/newbuildingFlight';
import { selectBannerTypeRotation } from '../../../selectors/selectBannerTypeRotation';
import { selectSuburbanBlackFridayBannerUrl } from '../../../selectors/suburbanBlackFriday';
import { EDealType } from '../../../types/dealType';
import { useApplicationContext } from '../../../utils/applicationContext';
import { FOfferType, isSuburban } from '../../../utils/category';
import { getNewbuildingEntryBannerInfo } from '../../../utils/getNewbuildingEntryBannerInfo';
import { NewbuildingBrokerSelfRequestFormBannerContainer } from '../../components/NewbuildingBrokerSelfRequestFormBanner';
import {
  NewbuildingEntryResaleBlock,
  trackNewbuildingEntryResaleBlockShow,
  trackNewbuildingEntryResaleBlockButtonClick,
  trackNewbuildingEntryResaleBlockItemClick,
} from '../../components/NewbuildingEntryResaleBlock';
import { NewbuildingFlightBannerContainer } from '../../components/NewbuildingFlightBanner';
import { SuburbanBlackFridayBannerContainerLoadable } from '../../components/SuburbanBlackFridayBanner';
import { SuburbanBuildersProjectsBanner } from '../../components/SuburbanBuildersProjectsBanner';
import { CianDobroBannerContainer } from '../CianDobroBannerContainer';
import { DealSaleBannerContainer } from '../DealSaleBannerContainer';
import { MortgageBrokerBannerContainer } from '../MortgageBrokerBannerContainer';
import { NewbuildingBrokerConsultantBannerContainer } from '../NewbuildingBrokerConsultantBannerContainer';
import { ServicePlacesMortgageBannerContainer } from '../ServicePlacesMortgageBannerContainer';
import { SubsidisedMortgageBannerContainer } from '../SubsidisedMortgageBannerContainer';
import { CianGptBannerContainer } from 'shared/containers/CianGptBannerContainer';
import { NewbuildingOffersCarouselContainer } from 'shared/containers/NewbuildingOffersCarouselContainer';
import { selectIsSelfRequestFormExperiment } from 'shared/selectors/experiments/selectIsSelfRequestFormExperiment';
import { selectIsNewbuildingOffersWithBannerCarouselVisible } from 'shared/selectors/newbuildingOffersCarousel';
import { selectRandomBoolean } from 'shared/selectors/randomBoolean';
import { IIntegrateAdOptions } from 'shared/serp/components/offers/types';
import { DailyrentBannerContainer } from 'shared/serp/containers/DailyrentBannerContainer';

export interface IBannerConatinerProps {
  options: IIntegrateAdOptions;
  position: number;
  isAgent?: boolean;
  locationId?: number;
  isCountrysideBuildersBannerAvailable?: boolean;
  isVisibleNewbuildingCarousel?: boolean;
}

export function BannerContainer({
  options,
  position,
  isAgent,
  isCountrysideBuildersBannerAvailable,
  isVisibleNewbuildingCarousel,
}: IBannerConatinerProps) {
  const isNewbuildingBannerRotationEnabled = useSelector(getIsNewbuildingBannerRotationEnabled);
  const bannerTypeRotation = useSelector(selectBannerTypeRotation);
  const isNewbuildingOrMixedListing = useSelector(selectIsNewbuildingOrMixedListing);
  const isNewbuildingFlightBannerAvailable = useSelector(selectNewbuildingFlightBannerAvailable);
  const isNewbuildingBrokerConsultantBannerAvailable = useSelector(selectNewbuildingBrokerConsultantBannerAvailable);
  const dealType = useSelector(getDealType);
  const offerType = useSelector(getOfferType);
  const jsonQuery = useSelector(getJsonQuery);
  const nbSimilarOffers = useSelector(getNewbuildingSimilarOffers);
  const nbSimilarAdPath = useSelector(getNewbuildingSimilarAdPath);
  const { logger, config } = useApplicationContext();
  const isMortgageBannerEnabled = config.get<boolean>('mortgage.isSerpBannerEnabled');
  const isDiscountMortgageBannerEnabled = config.get<boolean>('newbuildingDiscountMortgageBanner.Enabled');
  const suburbanBlackFridayUrl = useSelector(selectSuburbanBlackFridayBannerUrl);
  const isCianGptBannerEnabled = config.get<boolean>('audience.cianGPTFlight');

  const randomBoolean = useSelector(selectRandomBoolean);
  const isNewbuildingOffersWithBannerCarouselVisible = useSelector(selectIsNewbuildingOffersWithBannerCarouselVisible);
  const isSelfRequestFormExperiment = useSelector(selectIsSelfRequestFormExperiment);

  const [hideCarouselBanner, setHideCarouselBanner] = React.useState(false);
  const prevJsonQueryRef = React.useRef(jsonQuery);

  // Реанимируем карусель при изменении jsonQuery, если условия снова подходят для отображения
  React.useEffect(() => {
    const hasJsonQueryChanged = prevJsonQueryRef.current !== jsonQuery;

    if (hasJsonQueryChanged && hideCarouselBanner) {
      const shouldShowCarousel =
        isNewbuildingOffersWithBannerCarouselVisible &&
        !options.suggestionFragment?.isSimilar &&
        isVisibleNewbuildingCarousel;

      if (shouldShowCarousel) {
        setHideCarouselBanner(false);
      }
    }

    prevJsonQueryRef.current = jsonQuery;
  }, [
    jsonQuery,
    hideCarouselBanner,
    isNewbuildingOffersWithBannerCarouselVisible,
    options.suggestionFragment?.isSimilar,
    isVisibleNewbuildingCarousel,
  ]);

  const rotationBanner = React.useMemo(() => {
    switch (bannerTypeRotation) {
      case EBannerType.DealSale:
        return <DealSaleBannerContainer />;
      case EBannerType.MortgageBroker:
        return <MortgageBrokerBannerContainer />;
      case EBannerType.SubsidisedMortgage:
        return <SubsidisedMortgageBannerContainer />;
      case EBannerType.CianDobro:
        return <CianDobroBannerContainer />;
      case EBannerType.RentKpnCallback:
      case EBannerType.RentNBListing:
        return <NewbuildingRentBannerContainer bannerType={bannerTypeRotation} />;
      case EBannerType.SuburbanNBTrap:
        return <NewbuildingSuburbanSaleBannerContainer bannerType={bannerTypeRotation} />;
      case EBannerType.FlatSaleNBTrap:
        return <NewbuildingFlatSaleBannerContainer bannerType={bannerTypeRotation} />;
      case EBannerType.DailyRent:
        return <DailyrentBannerContainer />;
      default:
        return null;
    }
  }, [bannerTypeRotation]);

  React.useEffect(
    /* istanbul ignore next */ () => {
      if (!isCountrysideBuildersBannerAvailable) {
        removeCookie('buildersBannerShown');
      }
    },
    [isCountrysideBuildersBannerAvailable],
  );

  if (
    isNewbuildingOffersWithBannerCarouselVisible &&
    !hideCarouselBanner &&
    !options.suggestionFragment?.isSimilar &&
    isVisibleNewbuildingCarousel
  ) {
    return (
      <NewbuildingOffersCarouselContainer
        onError={() => setHideCarouselBanner(true)}
        onNoData={() => setHideCarouselBanner(true)}
      />
    );
  }

  if (
    position === 2 &&
    randomBoolean &&
    isCianGptBannerEnabled &&
    [FOfferType.Flat, FOfferType.FlatOld].includes(offerType)
  ) {
    return <CianGptBannerContainer />;
  }

  // Определяем, нужно ли показывать новостроечный баннер в новостроечной и смешанной выдаче
  if (isNewbuildingBannerRotationEnabled && !isAgent && isNewbuildingOrMixedListing) {
    if (isNewbuildingFlightBannerAvailable) {
      return <NewbuildingFlightBannerContainer />;
    }

    if (isDiscountMortgageBannerEnabled) {
      return <DiscountMortgageBannerContainer />;
    }

    if (isNewbuildingBrokerConsultantBannerAvailable) {
      // TODO: В случае выигрыша эксперимента удалить условие isNewbuildingBrokerConsultantBannerAvailable в CD-243911.
      if (isSelfRequestFormExperiment) {
        return <NewbuildingBrokerSelfRequestFormBannerContainer />;
      }

      return <NewbuildingBrokerConsultantBannerContainer />;
    }

    return null;
  }

  // Определяем, нужно ли показывать блоки объявлений из новостроек в выдаче квартир вторички
  if (
    isNewbuildingBannerRotationEnabled &&
    nbSimilarOffers &&
    nbSimilarAdPath &&
    offerType === FOfferType.FlatOld &&
    dealType === EDealType.Sale
  ) {
    const { title, description } = getNewbuildingEntryBannerInfo('resale', config, logger);

    return (
      <NewbuildingEntryResaleBlock
        onShow={trackNewbuildingEntryResaleBlockShow(jsonQuery)}
        onButtonClick={trackNewbuildingEntryResaleBlockButtonClick(jsonQuery)}
        onItemClick={trackNewbuildingEntryResaleBlockItemClick(jsonQuery)}
        url={nbSimilarAdPath}
        title={title}
        description={description}
        items={nbSimilarOffers}
      />
    );
  }

  // Если в загородке в продаже акция "Черная пятница" активна, то показываем баннер
  if (isSuburban(offerType) && dealType === EDealType.Sale && suburbanBlackFridayUrl) {
    return <SuburbanBlackFridayBannerContainerLoadable href={suburbanBlackFridayUrl} />;
  }

  if (!isNewbuildingBannerRotationEnabled && (offerType & FOfferType.Land) !== 0 && dealType === EDealType.Sale) {
    return <SuburbanBuildersProjectsBanner />;
  }

  if (rotationBanner) {
    return rotationBanner;
  }

  // Если ипотека выключена или позиция не 2, 32, 62, то ничего не отображаем
  if (!isMortgageBannerEnabled || ![2, 32, 62].includes(position)) {
    return null;
  }

  // В остальных случаях показываем банер ипотеки
  return <ServicePlacesMortgageBannerContainer options={options} position={position} />;
}
